import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { fetchCustomerMe, fetchUserByLogin } from "../services/meusDadosApi";
import type { UserInfo } from "../types";

type SelectedUnit = { hasCurrentHouseholder?: boolean };

export function useMeusDadosPage(): {
  loading: boolean | undefined;
  userInfo: UserInfo | undefined;
  setUserInfo: Dispatch<SetStateAction<UserInfo | undefined>>;
  userContacts: unknown[];
  emailAwait: string;
  setEmailAwait: Dispatch<SetStateAction<string>>;
  phoneAwait: string;
  setPhoneAwait: Dispatch<SetStateAction<string>>;
  openAUCModal: boolean;
  setOpenAUCModal: Dispatch<SetStateAction<boolean>>;
  openNameSocialModal: boolean;
  setOpenNameSocialModal: Dispatch<SetStateAction<boolean>>;
  openBirthDateModal: boolean;
  setOpenBirthDateModal: Dispatch<SetStateAction<boolean>>;
  openEditEmailModal: boolean;
  setOpenEditEmailModal: Dispatch<SetStateAction<boolean>>;
  openEditPhoneModal: boolean;
  setOpenEditPhoneModal: Dispatch<SetStateAction<boolean>>;
  openEmailConfirmModal: boolean;
  setOpenEmailConfirmModal: Dispatch<SetStateAction<boolean>>;
  openTokenPhoneModal: boolean;
  setOpenTokenPhoneModal: Dispatch<SetStateAction<boolean>>;
  birthDate: string | null;
  phoneNumber: string | null;
  hasEmailCheck: boolean;
  selectedUnit: SelectedUnit;
} {
  const [loading, setLoading] = useState<boolean>();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userContacts] = useState([]);
  const [emailAwait, setEmailAwait] = useState("");
  const [phoneAwait, setPhoneAwait] = useState("");
  const [openAUCModal, setOpenAUCModal] = useState(false);
  const [openNameSocialModal, setOpenNameSocialModal] = useState(false);
  const [openBirthDateModal, setOpenBirthDateModal] = useState(false);
  const [openEditEmailModal, setOpenEditEmailModal] = useState(false);
  const [openEditPhoneModal, setOpenEditPhoneModal] = useState(false);
  const [openEmailConfirmModal, setOpenEmailConfirmModal] = useState(false);
  const [openTokenPhoneModal, setOpenTokenPhoneModal] = useState(false);

  const selectedUnit: SelectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const responseUserInfo = await fetchCustomerMe();
        const responseUserId = await fetchUserByLogin();
        setUserInfo({ ...responseUserInfo, ...responseUserId } as UserInfo);
        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        const erro = err.response;
        if (erro) {
          const { message } = err.response.data;
          if (err.response.status !== 401) {
            toast.error(message);
          }
        } else if (err.response?.status !== 401) {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      }
    })();
  }, []);

  const birthDate = useMemo(() => {
    if (userInfo && userInfo.birthDate) {
      const dateSplit = userInfo.birthDate.split("-");
      return `${dateSplit[2]}/${dateSplit[1]}/${dateSplit[0]}`;
    }
    return null;
  }, [userInfo]);

  const phoneNumber = useMemo(() => {
    if (userInfo && userInfo.phoneDdd && userInfo.phoneNumber) {
      return `(${userInfo.phoneDdd}) ${userInfo.phoneNumber.slice(
        0,
        5
      )}-${userInfo.phoneNumber.slice(5)}`;
    }
    return null;
  }, [userInfo]);

  const hasEmailCheck = useMemo(() => {
    if (userContacts.length) {
      const userContactsEmails =
        userContacts.filter(
          (contact: { contactType?: string }) => contact.contactType === "EMAIL"
        ) || [];
      return !userContactsEmails.length
        ? false
        : userContactsEmails.every(
            (contact: { isValidated?: boolean }) =>
              contact.isValidated === false
          );
    }
    return false;
  }, [userContacts]);

  return {
    loading,
    userInfo,
    setUserInfo,
    userContacts,
    emailAwait,
    setEmailAwait,
    phoneAwait,
    setPhoneAwait,
    openAUCModal,
    setOpenAUCModal,
    openNameSocialModal,
    setOpenNameSocialModal,
    openBirthDateModal,
    setOpenBirthDateModal,
    openEditEmailModal,
    setOpenEditEmailModal,
    openEditPhoneModal,
    setOpenEditPhoneModal,
    openEmailConfirmModal,
    setOpenEmailConfirmModal,
    openTokenPhoneModal,
    setOpenTokenPhoneModal,
    birthDate,
    phoneNumber,
    hasEmailCheck,
    selectedUnit,
  };
}
